import React, { useMemo, useState } from "react";
import { superService } from "../../services";
import { useI18n } from "../../i18n";
import { Button, DataTable, Input, PageHead, Select, StatCard, baht, formatDate, useAsync } from "../../components/ui";
import Icon from "../../components/ui/Icon";

export const SALE_CATEGORIES = {
  plan: { th: "แพ็กเกจหลัก", en: "Plan" },
  addon: { th: "ฟีเจอร์เสริม / โควตา", en: "Add-on / quota" },
  points: { th: "Point ปั้ม", en: "Boost points" },
  ai_credit: { th: "เครดิต AI", en: "AI credits" },
  service: { th: "บริการติดตั้ง & อบรม", en: "Setup & training" },
  custom: { th: "ฟีเจอร์ custom / ใบเสนอราคา", en: "Custom / quoted" },
};

const dayStart = (value) => (value ? new Date(`${value}T00:00:00`) : null);
const dayEnd = (value) => (value ? new Date(`${value}T23:59:59`) : null);

const csvCell = (value) => `"${String(value ?? "").replace(/"/g, '""')}"`;

// ยอดขายทุกช่องทางของทุกร้าน (ราคายังไม่รวม VAT) — ดาวน์โหลด CSV ไปออกใบแจ้งหนี้ได้
export default function SuperSales() {
  const { tx } = useI18n();
  const { data: sales, loading } = useAsync(() => superService.sales(), [], []);
  const [filter, setFilter] = useState({ from: "", to: "", category: "", search: "" });

  const rows = useMemo(() => {
    const from = dayStart(filter.from);
    const to = dayEnd(filter.to);
    const keyword = filter.search.trim().toLowerCase();
    return (sales || []).filter((row) => {
      const date = new Date(row.date || 0);
      if (from && date < from) return false;
      if (to && date > to) return false;
      if (filter.category && row.category !== filter.category) return false;
      if (keyword && ![row.orgName, row.item, row.refNo].some((value) => String(value || "").toLowerCase().includes(keyword))) return false;
      return true;
    });
  }, [sales, filter]);

  const total = rows.reduce((sum, row) => sum + row.amount, 0);
  const byCategory = Object.keys(SALE_CATEGORIES).map((key) => ({ key, amount: rows.filter((row) => row.category === key).reduce((sum, row) => sum + row.amount, 0) }));

  const download = () => {
    const header = ["วันที่", "ร้าน", "org_id", "หมวด", "รายการ", "ยอด (บาท ไม่รวม VAT)", "Ref", "Transaction"];
    const lines = rows.map((row) =>
      [formatDate(row.date, true), row.orgName, row.orgId, SALE_CATEGORIES[row.category]?.th || row.category, row.item, row.amount, row.refNo, row.transactionId].map(csvCell).join(",")
    );
    // BOM ให้ Excel อ่านภาษาไทยถูก
    const blob = new Blob([`﻿${[header.map(csvCell).join(","), ...lines].join("\n")}`], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `sales-${filter.from || "all"}-${filter.to || "now"}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <>
      <PageHead
        title={tx("ยอดขาย", "Sales")}
        subtitle={tx("ทุกการซื้อที่ชำระสำเร็จของทุกร้าน (ราคายังไม่รวม VAT 7%) · แพ็กเกจที่ทีมงานเปิดให้ไม่นับเป็นยอดขาย", "Every successful purchase across shops (excl. 7% VAT) · team-granted plans are not counted")}
        actions={<Button variant="primary" disabled={!rows.length} onClick={download}><Icon name="download" /> {tx("ดาวน์โหลด CSV", "Download CSV")}</Button>}
      />

      <div className="grid-3 u-mb-16">
        <StatCard label={tx("ยอดรวม", "Total")} value={baht(total, 0)} icon={<Icon name="money" />} hint={tx(`${rows.length} รายการ`, `${rows.length} items`)} />
        {byCategory
          .filter((row) => row.amount > 0)
          .sort((a, b) => b.amount - a.amount)
          .slice(0, 2)
          .map((row) => (
            <StatCard key={row.key} label={tx(SALE_CATEGORIES[row.key].th, SALE_CATEGORIES[row.key].en)} value={baht(row.amount, 0)} icon={<Icon name="chartBar" />} />
          ))}
      </div>

      <div className="grid-4 u-mb-16" style={{ gap: 12 }}>
        <Input label={tx("ตั้งแต่", "From")} type="date" value={filter.from} onChange={(event) => setFilter({ ...filter, from: event.target.value })} />
        <Input label={tx("ถึง", "To")} type="date" value={filter.to} onChange={(event) => setFilter({ ...filter, to: event.target.value })} />
        <Select
          label={tx("หมวด", "Category")}
          placeholder={tx("ทุกหมวด", "All")}
          value={filter.category}
          options={Object.entries(SALE_CATEGORIES).map(([value, label]) => ({ value, label: tx(label.th, label.en) }))}
          onChange={(event) => setFilter({ ...filter, category: event.target.value })}
        />
        <Input label={tx("ค้นหา", "Search")} placeholder={tx("ร้าน / รายการ / Ref", "Shop / item / ref")} value={filter.search} onChange={(event) => setFilter({ ...filter, search: event.target.value })} />
      </div>

      <DataTable
        loading={loading}
        rows={rows}
        columns={[
          { key: "date", label: tx("วันที่", "Date"), render: (row) => formatDate(row.date, true) },
          { key: "orgName", label: tx("ร้าน", "Shop") },
          { key: "category", label: tx("หมวด", "Category"), render: (row) => tx(SALE_CATEGORIES[row.category]?.th || row.category, SALE_CATEGORIES[row.category]?.en || row.category) },
          { key: "item", label: tx("รายการ", "Item") },
          { key: "refNo", label: "Ref", render: (row) => row.refNo || "-" },
          { key: "amount", label: tx("ยอด", "Amount"), align: "right", render: (row) => baht(row.amount, 0) },
        ]}
        emptyText={tx("ไม่มียอดขายในช่วงนี้", "No sales in this range")}
      />
    </>
  );
}
